import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import { ImagePlus, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveImageUrl } from "@/components/ImageUploader";

const CATEGORY_LABEL = {
  cakes: "Cakes",
  cupcakes: "Cupcakes",
  cookies: "Cookies",
  brownies: "Brownies",
  cake_pops: "Cake Pops",
  mini_cakes: "Mini Cakes",
  other: "Sweets",
};

// Bento tile classes for visual variety
const TILE_CLASSES = [
  "md:col-span-7 md:row-span-2 aspect-square md:aspect-auto",
  "md:col-span-5 aspect-[4/3]",
  "md:col-span-5 aspect-[4/3]",
  "md:col-span-4 aspect-square",
  "md:col-span-4 aspect-square",
  "md:col-span-4 aspect-square",
];

export default function MenuGrid({ content }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/menu").then((r) => setItems(r.data)).catch((e) => console.error("menu load failed", e));
  }, []);

  return (
    <section id="menu" data-testid={HOME.menuSection} className="max-w-6xl mx-auto px-6 py-20">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-blue-800 mb-2">{content?.menu_eyebrow || "Our Menu"}</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-stone-900">{content?.menu_heading || "Handcrafted sweetness, any occasion."}</h2>
        </div>
        <p className="text-stone-500 max-w-sm">
          {content?.menu_description || "From layered wedding cakes to bite-size cake pops — everything is made to order and customized to your celebration."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 md:auto-rows-[200px]">
        {items.map((item, i) => (
          <MenuTile key={item.id} item={item} className={TILE_CLASSES[i % TILE_CLASSES.length]} />
        ))}
      </div>
    </section>
  );
}

function MenuTile({ item, className }) {
  const gallery = (item.images && item.images.length ? item.images : (item.image_url ? [item.image_url] : []));
  const [idx, setIdx] = useState(0);
  const total = gallery.length;
  const step = (dir) => (e) => {
    e.preventDefault(); e.stopPropagation();
    if (total <= 1) return;
    setIdx((cur) => (cur + dir + total) % total);
  };

  return (
    <div
      data-testid={`menu-tile-${item.id}`}
      className={`group relative rounded-3xl overflow-hidden card-lift bg-white border border-stone-100 ${className}`}
    >
      {total > 0 ? (
        <>
          {gallery.map((url, i) => (
            <img
              key={`${url}-${i}`}
              src={resolveImageUrl(url)}
              alt={`${item.name} ${i + 1}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${i === idx ? "opacity-100" : "opacity-0"}`}
              data-testid={`menu-tile-image-${item.id}-${i}`}
            />
          ))}
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={step(-1)}
                aria-label="Previous photo"
                data-testid={`menu-tile-prev-${item.id}`}
                className="absolute top-1/2 left-2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/80 backdrop-blur text-stone-700 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white z-10"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={step(1)}
                aria-label="Next photo"
                data-testid={`menu-tile-next-${item.id}`}
                className="absolute top-1/2 right-2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/80 backdrop-blur text-stone-700 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white z-10"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <div className="absolute top-3 right-3 rounded-full bg-black/45 backdrop-blur text-white text-[10px] font-medium px-2 py-0.5 z-10" data-testid={`menu-tile-count-${item.id}`}>
                {idx + 1}/{total}
              </div>
              <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-1.5 z-10" data-testid={`menu-tile-dots-${item.id}`}>
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
                    aria-label={`Go to photo ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-6" : "bg-white/60 w-1.5 hover:bg-white/80"}`}
                  />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="absolute inset-0 photo-placeholder flex-col gap-2">
          <ImagePlus className="h-6 w-6" />
          <span>Add photo here</span>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-900/10 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 p-5 text-white pointer-events-none">
        <p className="text-xs uppercase tracking-widest text-amber-200 mb-1">{CATEGORY_LABEL[item.category] || "Sweets"}</p>
        <h3 className="font-serif-display text-2xl leading-tight">{item.name}</h3>
        {item.description && <p className="text-xs text-white/80 mt-1 line-clamp-2">{item.description}</p>}
        {item.price_from > 0 && (
          <p className="text-sm text-white/90 mt-2">from <span className="font-medium">${item.price_from.toFixed(2)}</span></p>
        )}
      </div>
    </div>
  );
}
